"use client"

import { useState } from "react"
import { Check, ThumbsUp } from "lucide-react"

import { cn } from "@/lib/utils"
import { trackEvent, type EventProperties } from "@/lib/analytics"
import { verifyAnalysis } from "@/lib/verify-analysis-client"

interface ResultConfidenceFeedbackProps {
  grassType: string
  confidence: number
  /**
   * Saved analysis_history row id. When present, a "Yes, that's it" answer is
   * written to the verified-label pipeline as verification_source='confirmed'
   * (the labeling dataset that gates calibration work — see
   * obsidian/decisions/2026-07-11-grass-id-benchmark-pivot.md). Null/omitted
   * (unsaved anon result) skips the write; the analytics event still fires.
   */
  analysisId?: string | null
  /**
   * Fired after the user answers. `positive=false` means "Not quite" — the
   * parent reveals the alternatives picker so the user isn't left at a dead end.
   */
  onResponse: (positive: boolean) => void
  /** Headline question. Defaults to the grass-ID wording; diagnose passes its
   * own ("Does this look like your problem?"). */
  question?: string
  /** Confirmation shown after "Yes". Defaults to the grass-ID plan wording. */
  positiveText?: string
  /** Shown after "Not quite". Defaults to the grass-ID "pick the closest match"
   * wording, which doesn't fit diagnose — pass a mode-appropriate string. */
  negativeText?: string
  /** Diagnose uses its own event name so disease feedback never contaminates
      the grass-identification accuracy dataset. */
  eventName?: "result_confidence_feedback" | "diagnosis_confidence_feedback"
  eventProperties?: EventProperties
  /** Diagnosis rows are not grass labels and must never call the identify-only
      verification endpoint. Defaults from eventName for safe reuse. */
  verifyLabel?: boolean
  className?: string
}

/**
 * Post-result "confidence challenge" — the [[nps-surveys]] play applied to the
 * grass-ID result. Instead of an abstract 0-10 scale, it asks one specific
 * question tied to the moment ("Does this match your lawn?") with two concrete
 * choices. Captures real-world ID accuracy and gives the user an honest exit
 * when the match is wrong. It belongs beside every verdict, not only confident
 * ones, because confident mistakes are the most valuable corrections.
 */
export function ResultConfidenceFeedback({
  grassType,
  confidence,
  analysisId,
  onResponse,
  question = "Does this look like your lawn?",
  positiveText = `Great. Your ${grassType} plan is dialed in.`,
  negativeText = "No problem. Pick the closest match below and we'll rebuild your plan around it.",
  eventName = "result_confidence_feedback",
  eventProperties,
  verifyLabel = eventName === "result_confidence_feedback",
  className,
}: ResultConfidenceFeedbackProps) {
  const [answered, setAnswered] = useState<"yes" | "no" | null>(null)

  const handleAnswer = (positive: boolean) => {
    setAnswered(positive ? "yes" : "no")
    trackEvent(eventName, {
      grass_type: grassType,
      confidence,
      matched: positive,
      ...eventProperties,
    })
    // Confirmed label — non-blocking, but the telemetry reports what the
    // server actually acknowledged (anon rows can legitimately 404: viewer
    // isn't the creator, row is stale, or the label is already terminal).
    // The negative path writes its label from the picker instead
    // (selectAlternative → 'corrected'), which upgrades over 'confirmed'.
    if (verifyLabel && positive && analysisId) {
      void verifyAnalysis(analysisId, grassType, "confirmed").then((ok) => {
        trackEvent("verified_label_write", {
          source: "confirmed",
          grass_type: grassType,
          ok,
        })
      })
    }
    onResponse(positive)
  }

  if (answered === "yes") {
    return (
      <div
        role="status"
        className={cn(
          "flex items-center justify-center gap-2 rounded-xl border border-green-100 bg-green-50/60 px-4 py-3",
          className,
        )}
      >
        <Check className="w-4 h-4 text-green-600 flex-shrink-0" strokeWidth={2.5} />
        <span className="text-[13px] font-medium text-green-800">
          {positiveText}
        </span>
      </div>
    )
  }

  if (answered === "no") {
    return (
      <div
        className={cn(
          "rounded-xl border border-gray-200 bg-white px-4 py-3",
          className,
        )}
      >
        <span className="text-[13px] text-gray-600">
          {negativeText}
        </span>
      </div>
    )
  }

  return (
    <div
      className={cn(
        "flex flex-col items-center gap-2.5 rounded-xl border border-gray-200 bg-white px-4 py-3 sm:flex-row sm:justify-between",
        className,
      )}
    >
      <span className="text-[13.5px] font-semibold text-gray-900 text-center sm:text-left">
        {question}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => handleAnswer(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-green-200 bg-green-50 px-3 py-1.5 text-[13px] font-semibold text-green-700 hover:border-green-300 hover:bg-green-100 focus:outline-hidden focus:ring-2 focus:ring-green-500/30 transition-colors"
        >
          <ThumbsUp className="w-3.5 h-3.5" strokeWidth={2.25} />
          Yes, that&apos;s it
        </button>
        <button
          type="button"
          onClick={() => handleAnswer(false)}
          className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-[13px] font-semibold text-gray-600 hover:border-gray-300 hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-gray-400/30 transition-colors"
        >
          Not quite
        </button>
      </div>
    </div>
  )
}
